// The public booking pages' chrome (Phase B2). No app nav, no auth, no
// internal anything — a navy hero band with the wordmark, the phone
// fallback, and the screen's own headline, then the work below it, and
// the human fallback at the foot. Everything inside renders under
// .book-public, which scopes the client-facing palette (book.css).
//
// [2026-09-04] NAVY HERO across the whole flow (Grant: "the design of
// this page is bad… go with the navy hero, build it across the whole
// booking flow"). Every screen used to open on a bare centered H1 over
// the light-gray page; the advisor-choice step in particular reused the
// small topic-tile grid, so credentials wrapped to four lines and names
// ran off their cards. Now every page hands its headline to the shell
// (`hero`), the shell paints it on the brand's own hero treatment —
// navy gradient, grain, glow orbs, yellow swoosh on the last word — and
// the first card of each step lifts out of the band. One shell, one
// treatment: a step can't drift back to the old look by forgetting to
// style its own H1.

import { useState } from "react";
import type { ReactNode } from "react";

import { Check } from "lucide-react";

import { OFFICE_ADDRESS } from "../../../shared/scheduling/office-location";

import "./book.css";

/** Where the client is in the four-step routed flow (Topic · Who ·
 *  When · Details). "done" lights every step; omit it on screens that
 *  aren't part of that flow (pinned personal pages, invite links,
 *  manage links, dead ends). */
export type BookProgress = 1 | 2 | 3 | 4 | "done";

export interface BookHero {
  /** Small light-blue caps line above the title — the topic the client
   *  picked, carried through the flow as the "you're in the right
   *  place" thread. Rendered with a check mark. */
  eyebrow?: ReactNode;
  /** The page's one H1. Plain text: the last word gets the swoosh. */
  title: string;
  /** A bold white line under the title — the booked day and time on a
   *  confirmation, nothing on most screens. */
  kicker?: ReactNode;
  lede?: ReactNode;
  /** A quiet white link under the lede ("← Different topic"). */
  back?: ReactNode;
  progress?: BookProgress | undefined;
  /**
   * The step labels the dots stand for. Defaults to the front door's four
   * (Topic · Who · When · Details); the connected experience passes its
   * own ("When · Questions · Done" / "Questions · When · Done"), since a
   * personal link has no topic or who step to show (2026-09-05).
   */
  steps?: readonly string[] | undefined;
}

const STEP_LABELS = ["Topic", "Who", "When", "Details"] as const;

function ProgressSteps({
  progress,
  steps = STEP_LABELS,
}: {
  progress: BookProgress;
  steps?: readonly string[] | undefined;
}) {
  const current = progress === "done" ? steps.length + 1 : progress;
  return (
    <ol className="book-steps" aria-label="Booking progress">
      {steps.map((label, i) => {
        const n = i + 1;
        const state = n < current ? "done" : n === current ? "now" : "todo";
        return (
          <li
            key={label}
            className={`book-step-item book-step-item--${state}`}
            aria-current={state === "now" ? "step" : undefined}
          >
            <span className="book-step-dot" aria-hidden="true">
              {state === "done" ? <Check size={12} strokeWidth={2.5} /> : n}
            </span>
            <span className="book-step-label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** The H1 with the yellow swoosh under its last word — the brand's
 *  "exclamation point," used once per page and nowhere else. */
function SwooshTitle({ title }: { title: string }) {
  const words = title.trim().split(/\s+/);
  const last = words.pop() ?? "";
  return (
    <h1 className="book-hero-title">
      {words.length > 0 ? `${words.join(" ")} ` : ""}
      <span className="book-swoosh">
        {last}
        <svg viewBox="0 0 100 12" preserveAspectRatio="none" aria-hidden="true">
          <path
            d="M2 9 C 25 3, 60 2, 98 6"
            stroke="currentColor"
            strokeWidth="5"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      </span>
    </h1>
  );
}

export function BookShell({
  children,
  hero,
  wide,
}: {
  children?: ReactNode;
  /** The screen's headline, painted on the navy band. Every screen has
   *  one — a booking page with no H1 has no way to say where the client
   *  is. */
  hero: BookHero;
  /** The front door's two-panel booking layout needs real width on
   *  desktop; the invite/manage pages stay a comfortable single
   *  column. */
  wide?: boolean;
}) {
  return (
    <div className="book-public">
      <section className="book-hero">
        {/* Glow orbs — blue top-right, blue bottom-left, a whisper of
            yellow bottom-right. Never a background video: same warmth,
            zero LCP cost. */}
        <div className="book-orb book-orb--1" aria-hidden="true" />
        <div className="book-orb book-orb--2" aria-hidden="true" />
        <div className="book-orb book-orb--3" aria-hidden="true" />
        <div className={wide ? "book-shell book-shell--wide" : "book-shell"}>
          {/* Wordmark and the phone fallback share top billing on every
              screen (audit D2, 2026-08-25), now on the band itself. */}
          <header className="book-header">
            <span className="book-wordmark">Stewardship</span>
            <a className="book-header-call" href="tel:+16023842604">
              Call (602) 384-2604
            </a>
          </header>
          <div className="book-hero-inner">
            {hero.progress && <ProgressSteps progress={hero.progress} steps={hero.steps} />}
            {hero.eyebrow && (
              <div className="book-hero-eyebrow">
                <Check size={14} strokeWidth={2.25} aria-hidden="true" />
                <span>{hero.eyebrow}</span>
              </div>
            )}
            <SwooshTitle title={hero.title} />
            {hero.kicker && <div className="book-hero-kicker">{hero.kicker}</div>}
            {hero.lede && <p className="book-hero-lede">{hero.lede}</p>}
            {hero.back && <div className="book-hero-back">{hero.back}</div>}
          </div>
        </div>
      </section>
      <div className={wide ? "book-shell book-shell--wide" : "book-shell"}>
        {/* The first surface of every step lifts out of the band — so
            every step's content must START with a surface (a card, a
            grid of cards, a notice, a skeleton), never bare text. */}
        <main className="book-body">{children}</main>
      </div>
      <footer className="book-footer">
        Prefer to talk? Call the office at{" "}
        <a href="tel:+16023842604">(602) 384-2604</a> — we&rsquo;ll take care
        of you.
        <div className="book-footer-address">{OFFICE_ADDRESS}</div>
      </footer>
    </div>
  );
}

/** Initials avatar when no photo exists, or when one fails to load —
 *  never a broken-image icon. */
export function AdvisorAvatar({
  name,
  photoUrl,
}: {
  name: string;
  photoUrl: string | null;
}) {
  const [failed, setFailed] = useState(false);
  if (photoUrl && !failed) {
    return (
      <img
        className="book-avatar"
        src={photoUrl}
        alt=""
        onError={() => setFailed(true)}
      />
    );
  }
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] ?? "")
    .join("")
    .toUpperCase();
  return (
    <div className="book-avatar" aria-hidden="true">
      {initials || "S"}
    </div>
  );
}
